// TopMenuBar.jsx — rendered as a collapsible left sidebar
import React, { useEffect } from "react";
import { Box, Tooltip, Typography, Divider, useMediaQuery, useTheme, Drawer, IconButton } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BarChart as ReportsIcon,
  Upload as InputIcon,
  LibraryBooks as LibraryIcon,
  Science as CedexIcon,
  People as UsersIcon,
  Business as CompaniesIcon,
  Gavel as LicenseAgreementsIcon,
  Security as RolesIcon,
  ShowChart as UsageIcon,
  Dns as ServerIcon,
  PlayCircleOutline as RunsIcon,
  VpnKey as ApiKeyIcon,
  History as AuditLogIcon,
  RateReview as EvaluationsIcon,
  AccountTree as ProjectsIcon,
  Terminal as SqlDebugIcon,
  VpnLock as AuthSessionsIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useAuthStore } from "./store/authStore";
import { useSideNavStore, SIDEBAR_WIDE, SIDEBAR_NARROW } from "./store/sideNavStore";
import { hasCedexAccess, userRoles as userRolesOf } from "./utils/roles";

// ─── Nav item ─────────────────────────────────────────────────────────────────
function NavItem({ icon, label, to, collapsed, active }) {
  return (
    <Tooltip title={collapsed ? label : ""} placement="right" arrow>
      <Box
        component={Link}
        to={to}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: collapsed ? 0 : 2,
          py: 0.9,
          mx: 0.75,
          borderRadius: 1.5,
          justifyContent: collapsed ? "center" : "flex-start",
          textDecoration: "none",
          color: active ? "#fff" : "rgba(255,255,255,0.58)",
          backgroundColor: active ? "rgba(255,255,255,0.11)" : "transparent",
          position: "relative",
          transition: "background-color 0.13s ease, color 0.13s ease",
          "&:hover": {
            backgroundColor: active
              ? "rgba(255,255,255,0.15)"
              : "rgba(255,255,255,0.07)",
            color: "#fff",
          },
        }}
      >
        {active && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: "22%",
              bottom: "22%",
              width: 3,
              borderRadius: "0 2px 2px 0",
              backgroundColor: "#60A5FA",
            }}
          />
        )}
        {React.cloneElement(icon, { sx: { fontSize: 20, flexShrink: 0 } })}
        {!collapsed && (
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: active ? 600 : 400,
              letterSpacing: 0.1,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// ─── Sidebar content ──────────────────────────────────────────────────────────
function SidebarContent({ collapsed, onToggle }) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const location = useLocation();

  const userRoles = userRolesOf(user);
  const canUseCedex = hasCedexAccess(user);
  const isAdmin = userRoles.includes("Admin");
  const isInternalUser = userRoles.includes("InternalUser");
  const canReviewBioInterpreter = isAdmin || isInternalUser;

  // Match the prefix only at a path-segment boundary so e.g. /input doesn't
  // light up when the route is /input-library. We strip extra path segments
  // beyond the first one (so `/report-list/<id>` still matches the menu's
  // `/report-list/<userId>` entry), then require an exact match OR a trailing
  // slash on the longer path.
  const isActive = (target) => {
    const prefix = target.split("/").slice(0, 3).join("/");
    const path = location.pathname;
    return path === prefix || path.startsWith(prefix + "/");
  };

  const navItems = [
    { label: "Reports",        icon: <ReportsIcon />, to: `/report-list/${user?.id}` },
    ...(isAdmin
      ? [{ label: "Input Library",  icon: <LibraryIcon />, to: "/input-library" }]
      : []),
    { label: "Generate Input Template", icon: <InputIcon />,   to: "/input" },
    ...(canUseCedex
      ? [{ label: "CEDEX", icon: <CedexIcon />, to: "/cedex-import" }]
      : []),
    ...(canReviewBioInterpreter
      ? [
          { label: "Evaluations", icon: <EvaluationsIcon />, to: "/evaluations" },
          { label: "Projects", icon: <ProjectsIcon />, to: "/projects" },
        ]
      : []),
    // InternalUser needs a way to reach another user's report list to
    // author/read evaluations. Admin already surfaces Users from the
    // admin block below; add it here (only when the caller is
    // InternalUser but NOT Admin) so it appears in the main nav without
    // duplicating for Admins.
    ...(isInternalUser && !isAdmin
      ? [{ label: "Users", icon: <UsersIcon />, to: "/users" }]
      : []),
    // Runs is admin-block for Admins; InternalUser sees it here in the main
    // nav so they can watch runs across all users without gaining the rest
    // of the admin section. Backend gate matches: /run-list, /run/{id},
    // /run-request, PUT/DELETE /run/{id} require Admin or InternalUser
    // (run_api.py roles=['Admin','InternalUser']). Regular users have no
    // path to /runs — the nav is hidden AND the backend refuses.
    ...(isInternalUser && !isAdmin
      ? [{ label: "Runs", icon: <RunsIcon />, to: "/runs" }]
      : []),
  ];

  const adminItems = [
    { label: "Users",         icon: <UsersIcon />, to: "/users" },
    { label: "Companies",     icon: <CompaniesIcon />, to: "/companies" },
    { label: "License Agreements", icon: <LicenseAgreementsIcon />, to: "/license-agreements" },
    { label: "Roles",         icon: <RolesIcon />, to: "/role-list" },
    { label: "Usage",         icon: <UsageIcon />, to: "/usage" },
    { label: "Runs",          icon: <RunsIcon />, to: "/runs" },
    { label: "API Clients",   icon: <ApiKeyIcon />, to: "/api-clients" },
    { label: "Server Status", icon: <ServerIcon />,to: "/server-status" },
    { label: "Activity",      icon: <AuditLogIcon />,to: "/activity" },
    { label: "SQL Debug",     icon: <SqlDebugIcon />,to: "/sql-debug" },
    { label: "Auth Sessions", icon: <AuthSessionsIcon />,to: "/auth-sessions" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#1a2b44",
        overflow: "hidden",
      }}
    >
      {/* Logo + toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 2,
          minHeight: 56,
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Link to="/" style={{ display: "flex", alignItems: "center" }}>
            <img
              src="/Metalytics-Logo_light.webp"
              alt="Metalytics"
              style={{ height: 30 }}
            />
          </Link>
        )}
        <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"} placement="right">
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{
              color: "rgba(255,255,255,0.5)",
              "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.07)" },
            }}
          >
            {collapsed ? <ExpandIcon /> : <CollapseIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigation items */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1.5 }}>
        {navItems.map((item) => (
          <NavItem
            key={item.to}
            {...item}
            collapsed={collapsed}
            active={isActive(item.to)}
          />
        ))}

        {isAdmin && (
          <>
            <Box sx={{ px: collapsed ? 0 : 2, pt: 2, pb: 0.75 }}>
              {collapsed ? (
                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.28)",
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                  }}
                >
                  Admin
                </Typography>
              )}
            </Box>
            {adminItems.map((item) => (
              <NavItem
                key={item.to}
                {...item}
                collapsed={collapsed}
                active={isActive(item.to)}
              />
            ))}
          </>
        )}
      </Box>

      {/* User section */}
      <Box
        sx={{
          borderTop: "1px solid rgba(255,255,255,0.07)",
          py: 1,
          flexShrink: 0,
        }}
      >
        <Tooltip title={collapsed ? (user?.email || "Profile") : ""} placement="right">
          <Box
            component={Link}
            to="/profile"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: collapsed ? 0 : 2,
              py: 0.9,
              mx: 0.75,
              borderRadius: 1.5,
              justifyContent: collapsed ? "center" : "flex-start",
              textDecoration: "none",
              transition: "background-color 0.13s ease",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "#2F3F5C",
                border: "1.5px solid rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#fff",
                flexShrink: 0,
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || "?"}
            </Box>
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "rgba(255,255,255,0.75)",
                    fontWeight: 500,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user?.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>

        <Tooltip title={collapsed ? "Sign out" : ""} placement="right">
          <Box
            onClick={handleLogout}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: collapsed ? 0 : 2,
              py: 0.9,
              mx: 0.75,
              borderRadius: 1.5,
              justifyContent: collapsed ? "center" : "flex-start",
              color: "rgba(255,255,255,0.42)",
              cursor: "pointer",
              transition: "all 0.13s ease",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            {!collapsed && (
              <Typography sx={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                Sign out
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ─── Main export (still named TopMenuBar for backward compat) ─────────────────
export default function TopMenuBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { collapsed, toggle } = useSideNavStore();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Auto-collapse when viewport shrinks below 900px (md)
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  useEffect(() => {
    // Only auto-collapse if no explicit session pref was ever set
    if (sessionStorage.getItem("sidenav_collapsed") === null && isMedium) {
      useSideNavStore.getState().setCollapsed(true);
    }
  }, [isMedium]);

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            zIndex: 1300,
            backgroundColor: "#1a2b44",
            color: "#fff",
            width: 40,
            height: 40,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            "&:hover": { backgroundColor: "#2F3F5C" },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: { width: SIDEBAR_WIDE, border: "none" },
          }}
        >
          <SidebarContent collapsed={false} onToggle={() => setMobileOpen(false)} />
        </Drawer>
      </>
    );
  }

  const width = collapsed ? SIDEBAR_NARROW : SIDEBAR_WIDE;

  return (
    <Box
      component="nav"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width,
        zIndex: 1200,
        transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: "2px 0 16px rgba(0,0,0,0.22)",
        flexShrink: 0,
      }}
    >
      <SidebarContent collapsed={collapsed} onToggle={toggle} />
    </Box>
  );
}
